import { toast } from '../../dom';
import { fishingApi } from '../../game-api';
import { playSfx } from '../../audio/audio';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { ApiError } from '../../../core/network/api';
import { TILE } from '../../map/world';
import { FOOT_OFFSET } from '../../map/player';
import { dirToTile, refreshBag, type FarmCtx } from './context';
import { affordEnergy } from './resources';

/**
 * Fishing: tap the water from the bank.
 *
 * The game shipped with a `fish` animation row, a rod sprite, eight fish icons
 * and Fisherman Fin standing on a bank telling the player that "nobody has ever
 * pulled a fish out of this water". This is the mechanic those assets were
 * waiting for.
 *
 * WHERE YOU CAN FISH is decided here, on the client, because the world is
 * generated from a seed and the server has no map — the same split that already
 * governs chopping and mining. The server prices the cast and rolls the catch;
 * the client only answers "is that water, and am I close enough to reach it".
 */

/** How far the rod reaches, in tiles, from the hero's feet to the water. */
export const CAST_REACH = 2;

/**
 * Can the hero cast at this tile?
 *
 * Pure so the rule is testable — jsdom cannot drive Pixi, so this is where the
 * behaviour is pinned rather than in the tap plumbing. Returns the reason it
 * cannot, or null when it can, so every refusal can say something: a tap that
 * silently does nothing is indistinguishable from a broken rod, which is the
 * complaint every other refusal in this game has already had to answer.
 */
export function castBlocker(
  c: Pick<FarmCtx, 'world'> & { player?: { x: number; y: number } | null },
  tx: number,
  ty: number,
  hasRod: boolean,
): string | null {
  const world = c.world;
  if (!world) return 'Still loading…';
  if (!hasRod) return 'You need a rod — Fin sells one on the bank';
  if (tx < 0 || ty < 0 || tx >= world.cols || ty >= world.rows) return 'Outside the farm';
  if (world.terrain[ty * world.cols + tx] !== 'water') return 'Cast into the water';
  const p = c.player;
  if (!p) return 'Still loading…';
  const px = Math.floor(p.x / TILE), py = Math.floor((p.y - FOOT_OFFSET) / TILE);
  const dist = Math.max(Math.abs(tx - px), Math.abs(ty - py));
  if (dist > CAST_REACH) return 'Too far — stand on the bank';
  return null;
}

/**
 * One cast, with the hero facing the water.
 *
 * The animation is the pack's own `fish` row, which nothing had ever played —
 * it composites the rod as a tool overlay exactly as chopping composites the axe.
 */
export async function castAt(c: FarmCtx, tx: number, ty: number): Promise<void> {
  if (!affordEnergy(c, 'cast')) return;
  try {
    await dispatchAction('fishing.cast', async (actionId) => {
      void c.player?.playAction('fish', dirToTile(c, tx, ty));
      playSfx('water_pour');                      // the closest thing to a plop we ship
      const r = await fishingApi.cast(actionId);
      if (r.caught) {
        playSfx('harvest_crop');
        toast(`${r.name}!${r.leveledUp ? ' · LEVEL UP!' : ''}`);
      } else {
        // Say it plainly. An empty line that looked identical to a failed tap
        // would read as the feature being broken.
        toast('The line comes back empty…');
      }
      await c.game.refreshHud();
      refreshBag(c);
    }, { key: `${tx},${ty}` });
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not cast', 'error');
  }
}
